/**
 * NIP-31 `alt` — what an event says it is, for a client that cannot render it.
 *
 * This is the whole answer to a problem Nostr has structurally: kinds are an open
 * set, so every client is permanently a client that does not understand some of
 * what it receives. The choices for an unknown kind are to drop it, to render it as
 * a blank or broken row, or to show the author's own one-line description of it.
 * Only the third is honest — dropping it means a reader who was mentioned in
 * something never learns it exists, and a blank row reads as a bug in Setu.
 *
 * Two things this deliberately does not do:
 *
 *  - **No fallback text of our own.** If the author wrote no `alt`, this returns
 *    undefined and the caller says "an event of kind N" in its own words. Inventing
 *    a description here would put a sentence in the author's mouth.
 *  - **No length limit and no sanitising.** The string is a stranger's text heading
 *    for a row, so the caller clamps it the same way it clamps note bodies. Trimming
 *    it here would mean two clamps in the codebase that can disagree, and the render
 *    layer is the one that knows how much space it has.
 */

import { getTagged, type HasTags } from "./tags";

/**
 * The author's description of this event, if they gave one.
 *
 * First tag wins, and an empty value counts as absent: a bare `["alt"]` or
 * `["alt", ""]` describes nothing, and treating it as a description would render an
 * empty row that looks like a failed load rather than an unhandled kind.
 */
export function altText(event: HasTags): string | undefined {
  for (const tag of getTagged(event, "alt")) {
    const value = tag[1];
    if (value === undefined) continue;
    const trimmed = value.trim();
    if (trimmed === "") continue;
    return trimmed;
  }
  return undefined;
}
